import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import { Anchor, Box, Group, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { CollectionTile } from "./CollectionTile";
import { SubjectTile } from "@/components/subject/SubjectTile";
import { useStores } from "@/hooks/useStores";
import { Urls } from "@/core/urls";
import { featuredCollections, featuredSubjects } from "@/core/featured";
import { useLoad } from "@/hooks/useLoad";

/** Tiles in a row at tablet width and up; a phone shows the first two of them */
const TILES = 3;
/**
 * About how tall the shelf is once loaded, at a phone and at a wider width. It sits above the news feed, so without
 * this space reserved while the lists load, the feed shows near the top and then jumps down when they arrive.
 */
const SHELF_HEIGHT = { base: 1500, sm: 850 };

/**
 * A grid cell holds its tile as a grid of its own, which stretches the tile to the height of the row, so the tiles
 * in a row are equally tall. On a phone the last tile of a row is left out.
 */
const cellDisplay = (index: number) => (index < TILES - 1 ? "grid" : { base: "none", sm: "grid" });

const isPending = (status: string) => status === "idle" || status === "loading";

/**
 * The front page shelf that leads into the collections and focus areas: the collections that are most popular on the
 * chart, and the focus areas with a cover photo, as tiles.
 */
export const FeaturedCollections = observer(() => {
    const { collectionStore, subjectStore, toplistStore } = useStores();

    useLoad("lists", () => Promise.all([collectionStore.loadList(), subjectStore.loadList()]), "FeaturedCollections");

    // The chart decides which collections come first: wait for it (it is on its way for the page anyway) so the
    // tiles do not change places when it arrives. If it cannot be had, the collections rotate without it.
    const loading = [collectionStore.list.status, subjectStore.list.status, toplistStore.page.status].some(isPending);

    const collections = loading ? [] : featuredCollections(collectionStore.collections, toplistStore.toplist, TILES);
    const subjects = loading ? [] : featuredSubjects(subjectStore.subjects, TILES);

    if (!loading && collections.length === 0 && subjects.length === 0) {
        return null;
    }

    return (
        <Stack gap="xl" mb="xl" mih={loading ? SHELF_HEIGHT : undefined}>
            {collections.length > 0 && (
                <section aria-labelledby="home-collections">
                    <Group align="baseline" justify="space-between" mb="md">
                        <Title id="home-collections" order={2}>
                            Poddsamlingar
                        </Title>
                        <Anchor component={Link} size="sm" to={Urls.collections()}>
                            Alla samlingar →
                        </Anchor>
                    </Group>
                    <SimpleGrid cols={{ base: 1, sm: TILES }} spacing="md">
                        {collections.map((collection: Collection, index) => (
                            <Box display={cellDisplay(index)} key={collection.id}>
                                <CollectionTile collection={collection} />
                            </Box>
                        ))}
                    </SimpleGrid>
                </section>
            )}

            {subjects.length > 0 && (
                <section aria-labelledby="home-subjects">
                    <Group align="baseline" justify="space-between" mb="xs">
                        <Title id="home-subjects" order={2}>
                            Fokus
                        </Title>
                        <Anchor component={Link} size="sm" to={Urls.focusAreas()}>
                            Alla fokusområden →
                        </Anchor>
                    </Group>
                    <Text c="dimmed" mb="md" size="sm">
                        Avsnitt från flera poddar om ett och samma ämne.
                    </Text>
                    <SimpleGrid cols={{ base: 1, sm: TILES }} spacing="md">
                        {subjects.map((subject: Subject, index) => (
                            <Box display={cellDisplay(index)} key={subject.slug}>
                                <SubjectTile subject={subject} />
                            </Box>
                        ))}
                    </SimpleGrid>
                </section>
            )}
        </Stack>
    );
});
